import React, { memo, useMemo, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';

import { useTranslation } from 'react-i18next';

import {
  DashboardListContentPage,
  DataTableSearch,
  CsvImportBannerList,
  LockableMenuItem,
} from 'components';

import { PanelSelector } from './components/PanelSelector';
import { useSelector } from 'react-redux';
import {
  selectCurrentPanel,
  panelsSelector,
  getFastParticipantsForPanel,
} from 'store';
import { Feature } from '@app/api';
import { useFetchedEntity } from 'hooks/useFetched';
import { UseGlobalFiltersInstanceProps } from 'react-table';
import { MenuItem, MenuButton, useBoolModalState } from '@app/core';
import { PanelSubscriptionFormModal } from './components/PanelSubscriptionFormModal';
import { ImportCsvModal } from './components/ImportCsvModal';
import { Select } from '@app/form';
import { EmptyPanel } from './components/EmptyPanel';

const useStyles = createUseStyles((theme: Theme) => ({
  search: {
    marginRight: theme.marginBase,
  },
  buttonsDisplay: {
    display: 'flex',
    justifyContent: 'center',
    marginRight: -theme.marginBase * 2,
    [theme.media.mobile]: {
      flexDirection: 'column',
      marginRight: 0,
    },
  },
  pageSizeSelect: {
    maxWidth: theme.maxContentWidth.mediumBlock / 2,
    width: '100%',
  },
}));

interface Props {
  children: React.ReactNode;
  resetPane: () => any;
  tableInfos: UseGlobalFiltersInstanceProps<any> & {
    state: any;
    setPageSize: (pageSize: number) => void;
  };
  openCreation: () => any;
  participantsCount?: number;
}

const pageSizes = [10, 15, 20, 25, 50, 75, 100];

export const ParticipantListLayout = memo(
  ({
    resetPane,
    children,
    tableInfos,
    openCreation,
    participantsCount,
  }: Props) => {
    const theme: Theme = useTheme();
    const classes = useStyles({ theme });
    const { t } = useTranslation();

    const pageSizeOptions = pageSizes.map((size: number) => ({
      label: t('participants.list.pageSize.label', { count: size }),
      value: size,
    }));

    const panels = useSelector(panelsSelector);

    const panel = useSelector(selectCurrentPanel);
    const panelId = panel && panel.id;

    const { fetching } = useFetchedEntity(getFastParticipantsForPanel, panelId);

    const [
      panelSubFormModalOpen,
      openPanelSubFormModal,
      closePanelSubFormModal,
    ] = useBoolModalState();

    const [importCsvModalOpen, openImportCsvModal, closeImportCsvModal] =
      useBoolModalState();

    const [importingCsvId, setImportingCsvId] = useState<string | undefined>(
      undefined,
    );

    const actions = useMemo(
      () => [
        <div className={classes.pageSizeSelect}>
          <Select
            placeholder={t('participants.list.pageSize.placeholder')}
            onChange={(sizePage: { label: string; value: number }) => {
              tableInfos.setPageSize(sizePage.value);
            }}
            noMargin
            options={pageSizeOptions}
            smallSelect
          />
        </div>,
        <DataTableSearch tableInfos={tableInfos} className={classes.search} />,
        <MenuButton
          small
          key="add"
          text={t('participants.list.addButton.main')}
          color="accent"
        >
          <MenuItem
            onClick={openCreation}
            label={t('participants.list.addButton.manual')}
          />
          <LockableMenuItem
            onClick={openPanelSubFormModal}
            label={t('participants.list.addButton.form')}
            feature={Feature.PANEL_SUBSCRIPTION_FORM}
          />
          <LockableMenuItem
            onClick={openImportCsvModal}
            label={t('participants.list.addButton.csv')}
            feature={Feature.CSV_IMPORT}
          />
        </MenuButton>,
      ],
      [
        tableInfos,
        openCreation,
        classes,
        openImportCsvModal,
        openPanelSubFormModal,
        pageSizeOptions,
      ],
    );

    return (
      <React.Fragment>
        <DashboardListContentPage
          title={t('participants.list.title')}
          banner={
            <CsvImportBannerList
              csvImportId={importingCsvId}
              panelId={panelId}
            />
          }
          listEmpty={!participantsCount && panels.length < 2}
          fetching={fetching}
          scrollInside
          onClick={resetPane}
          titleSideElement={<PanelSelector />}
          actions={actions}
          emptyListContent={
            <EmptyPanel
              openPanelSubFormModal={openPanelSubFormModal}
              openImportCsvModal={openImportCsvModal}
              openCreation={openCreation}
            />
          }
        >
          {children}
        </DashboardListContentPage>

        <PanelSubscriptionFormModal
          isOpen={panelSubFormModalOpen}
          onRequestClose={closePanelSubFormModal}
          panelId={panelId}
        />
        {panel && (
          <ImportCsvModal
            isOpen={importCsvModalOpen}
            onRequestClose={closeImportCsvModal}
            panel={panel}
            onUploadSuccess={setImportingCsvId}
          />
        )}
      </React.Fragment>
    );
  },
);
